import { ApplicationRef, inject, Injectable, NgZone } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, interval } from 'rxjs';
import { first } from 'rxjs/operators';

import { AlertModalService } from '../../shared/form-elements/components/alert-modal/alert-modal.service';

@Injectable({ providedIn: 'root' })
export class CheckForUpdateService {

    private swUpdate = inject(SwUpdate);
    private appRef = inject(ApplicationRef);
    private zone = inject(NgZone);
    private alertModalService = inject(AlertModalService);

    isNewVersionAvailable: boolean = false;
    intervalSource = interval(4 * 60 * 60 * 1000); // every 4 hours

    constructor() { this.checkForUpdates(); }

    public checkForUpdates(): void { 
        if (!this.swUpdate.isEnabled) { return; }

        // Allow the app to stabilize first, before starting polling for updates with `interval()`.
        const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
        const onceAppIsStable$ = concat(appIsStable$, this.intervalSource);      

        this.zone.runOutsideAngular(() => {
            onceAppIsStable$.subscribe(async () => {
                this.intervalSource.subscribe(async () => {
                    try {
                        this.isNewVersionAvailable = await this.swUpdate.checkForUpdate();
                        if (this.isNewVersionAvailable) { this.promptUser(); }
                        console.log(this.isNewVersionAvailable ? 'A new version is available.' : 'Already on the latest version.');
                    } catch (error) {
                        console.error('Failed to check for updates:', error);
                    }
                });
            });
        });
    }

    applyUpdate(): void {
        // Reload the page to update to the latest version after the new version is activated
        this.swUpdate.activateUpdate()
            .then(() => document.location.reload())
            .catch(error => console.error('Failed to apply updates:', error));
    }

    promptUser(): void {
        this.alertModalService.openAlert(
            'A new version of the app is available. Please reload.',
            () => {
                this.swUpdate.activateUpdate()
                    .then(() => globalThis.location.reload())
                    .catch(error => console.error('Failed to apply updates:', error));
            }
        );
    }
}
